import { Link } from "react-router-dom";
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Search, CalendarPlus, Ban, CheckCircle, Pencil, Plus, Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { confirmar } from "@/components/ConfirmDialog";
import { RefreshButton } from "@/components/RefreshButton";
import { EnrollmentsDialog, STATE_META, fmtDate } from "@/components/EnrollmentsDialog";
import { Pager } from "@/components/Pager";

// último acesso: data/hora + "há quanto tempo", com cor (verde recente, laranja 1 a 4 semanas, vermelho mais de 30 dias)
const ultimoAcesso = (iso) => {
  if (!iso) return { txt: "Nunca acessou", sub: "", cls: "text-slate-400" };
  const d = new Date(iso), diff = (Date.now() - d.getTime()) / 1000;
  const ago = diff < 90 ? "agora" : diff < 3600 ? `há ${Math.floor(diff / 60)} min` : diff < 86400 ? `há ${Math.floor(diff / 3600)} h` : `há ${Math.floor(diff / 86400)} dia(s)`;
  const dias = diff / 86400;
  return { txt: d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }), sub: ago, cls: dias <= 7 ? "text-emerald-700" : dias <= 30 ? "text-amber-700" : "text-red-600" };
};

const addMonthsISO = (iso, n) => {
  const d = new Date((iso || new Date().toISOString().slice(0, 10)) + "T12:00:00");
  d.setMonth(d.getMonth() + n);
  return d.toISOString().slice(0, 10);
};

/** Nova matrícula: escolhe o aluno, um ou mais cursos e (opcional) as datas de acesso. */
function NewEnrollmentDialog({ open, courses, onClose, onDone }) {
  const [students, setStudents] = useState(null);
  const [search, setSearch] = useState("");
  const [stu, setStu] = useState(null);
  const [already, setAlready] = useState(new Set());
  const [picked, setPicked] = useState(new Set());
  const [starts, setStarts] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch(""); setStu(null); setPicked(new Set()); setAlready(new Set()); setStarts(""); setExpires("");
    if (students === null) api.get("/students").then((r) => setStudents(r.data)).catch(() => setStudents([]));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const matches = (() => {
    if (stu || !students || search.trim().length < 2) return [];
    const q = search.trim().toLowerCase();
    return students.filter((x) => (x.name || "").toLowerCase().includes(q) || (x.email || "").toLowerCase().includes(q)).slice(0, 8);
  })();

  const choose = (s) => {
    setStu(s); setSearch(s.name); setPicked(new Set());
    api.get(`/students/${s.id}/enrollments`).then((r) => setAlready(new Set(r.data.items.filter((i) => i.direct).map((i) => i.course_id)))).catch(() => setAlready(new Set()));
  };
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const save = async () => {
    setBusy(true);
    try {
      for (const cid of picked) {
        await api.put(`/students/${stu.id}/enrollments/${cid}`, { status: "ativa", starts_at: starts, expires_at: expires, direct: true });
      }
      toast.success(picked.size > 1 ? `${picked.size} matrículas criadas.` : "Matrícula criada.");
      onDone(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Nova matrícula</DialogTitle>
          <DialogDescription>Escolha o aluno e o curso. Sem datas, o acesso começa agora e não expira.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="space-y-1.5">
            <Label>Aluno</Label>
            <Input value={search} autoFocus onChange={(e) => { setSearch(e.target.value); setStu(null); setPicked(new Set()); }} placeholder="Digite o nome ou e-mail…" data-testid="new-enroll-student" />
            {students === null && <p className="text-xs text-slate-400">Carregando alunos…</p>}
            {matches.length > 0 && (
              <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-52 overflow-y-auto">
                {matches.map((s) => (
                  <button key={s.id} type="button" onClick={() => choose(s)} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm">
                    <div className="font-medium text-slate-800">{s.name}</div><div className="text-xs text-slate-400">{s.email}</div>
                  </button>
                ))}
              </div>
            )}
            {!stu && search.trim().length >= 2 && students && matches.length === 0 && <p className="text-xs text-slate-400">Nenhum aluno encontrado. Cadastre em Usuários → Alunos.</p>}
          </div>
          {stu && (
            <>
              <div className="space-y-1.5">
                <Label>Curso(s)</Label>
                <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
                  {courses.map((c) => {
                    const has = already.has(c.id);
                    return (
                      <label key={c.id} className={`flex items-center gap-3 px-3 py-2 text-sm ${has ? "opacity-50" : "cursor-pointer hover:bg-slate-50"}`}>
                        <input type="checkbox" disabled={has} checked={picked.has(c.id)} onChange={() => toggle(c.id)} />
                        <span className="flex-1">{c.title}</span>
                        {has && <span className="text-xs text-slate-500">já matriculado</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Início (opcional)</Label><Input type="date" value={starts} onChange={(e) => setStarts(e.target.value)} /></div>
                <div className="space-y-1.5"><Label>Válido até (opcional)</Label><Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} /></div>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="text-slate-400">Prazo rápido:</span>
                {[[3, "3 meses"], [6, "6 meses"], [12, "12 meses"]].map(([n, l]) => (
                  <button key={n} type="button" onClick={() => setExpires(addMonthsISO(starts, n))} className="px-2.5 py-1 rounded-md border border-slate-200 hover:border-[#1E9E2A] hover:text-[#1E9E2A] bg-white">{l}</button>
                ))}
                <button type="button" onClick={() => setExpires("")} className="px-2.5 py-1 rounded-md border border-slate-200 bg-white text-slate-500">Sem prazo</button>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!stu || picked.size === 0 || busy} onClick={save} className="text-white" style={{ background: "#1E9E2A" }} data-testid="new-enroll-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Matricular"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const STATES = [["", "Todas"], ["ativa", "Ativas"], ["expirada", "Expiradas"], ["bloqueada", "Bloqueadas"], ["agendada", "Agendadas"]];

export default function Enrollments() {
  const [data, setData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [query, setQuery] = useState("");
  const [courseId, setCourseId] = useState("");
  const [state, setState] = useState("");
  const [page, setPage] = useState(1);
  const [student, setStudent] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = useCallback(() => {
    setData(null);
    api.get("/enrollments", { params: { q: query, course_id: courseId, state, page, limit: 50 } })
      .then((r) => setData(r.data)).catch(() => setData({ total: 0, rows: [] }));
  }, [query, courseId, state, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => { setPage(1); setQuery(q); }, 350); return () => clearTimeout(t); }, [q]);

  const act = async (fn, ok) => {
    try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const extend = (r, days) => act(() => api.post(`/students/${r.student_id}/enrollments/${r.course_id}/extend`, { days }), `Prolongado em ${days} dias.`);
  const vitalicio = async (r) => {
    if (!(await confirmar(`Tornar o acesso de ${r.student_name} em "${r.course_title || "este curso"}" VITALÍCIO (sem data de validade)?`))) return;
    act(() => api.put(`/students/${r.student_id}/enrollments/${r.course_id}`, { status: r.status, starts_at: r.starts_at || "", expires_at: "" }), "Acesso vitalício liberado.");
  };
  const setStatus = (r, status) => act(() => api.put(`/students/${r.student_id}/enrollments/${r.course_id}`, {
    status, starts_at: r.starts_at, expires_at: r.expires_at,
  }), status === "bloqueada" ? "Matrícula bloqueada." : "Matrícula liberada.");


  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Matrículas</h1>
          <p className="text-slate-500 mt-1">Prazos de acesso por aluno e curso: prolongue, bloqueie ou ajuste as datas.</p>
        </div>
        <div className="flex items-center gap-2">
          <RefreshButton variant="outline" onClick={load} />
          <Button onClick={() => setNewOpen(true)} className="text-white" style={{ background: "#1E9E2A" }} data-testid="btn-new-enrollment"><Plus className="h-4 w-4 mr-1.5" /> Nova matrícula</Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px] basis-full lg:basis-auto">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar aluno por nome ou e-mail..." className="pl-9 h-10" />
        </div>
        <select value={courseId} onChange={(e) => { setPage(1); setCourseId(e.target.value); }} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm min-w-[220px]">
          <option value="">Todos os cursos</option>
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <div className="flex flex-wrap gap-1 bg-slate-100 rounded-lg p-1">
          {STATES.map(([v, l]) => (
            <button key={v || "all"} onClick={() => { setPage(1); setState(v); }}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${state === v ? "bg-white text-[#232D4F] shadow-sm" : "text-slate-500"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {data === null ? (
          <div className="p-4 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded" />)}</div>
        ) : data.rows.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">
            Nenhuma matrícula encontrada. Matricule alunos em <strong>Alunos → ⋮ → Matrículas</strong> ou pela turma.
          </div>
        ) : (
          <div className="overflow-x-auto iep-scroll">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/60">
                  <TableHead>Aluno</TableHead>
                  <TableHead>Curso</TableHead>
                  <TableHead className="hidden md:table-cell">Origem</TableHead>
                  <TableHead className="hidden lg:table-cell">Início</TableHead>
                  <TableHead>Válido até</TableHead>
                  <TableHead>Último acesso</TableHead>
                  <TableHead>Situação</TableHead>
                  <TableHead className="text-right">Opções</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((r) => {
                  const meta = STATE_META[r.state] || STATE_META.ativa;
                  return (
                    <TableRow key={`${r.student_id}-${r.course_id}`}>
                      <TableCell className="min-w-[220px]">
                        <Link to={`/admin/matriculas/${r.student_id}/${r.course_id}`} className="group flex items-center gap-3" data-testid={`enr-open-${r.student_id}`}>
                          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#1E9E2A] to-[#147a1f] text-sm font-bold text-white">{(r.student_name || "?").trim()[0]?.toUpperCase()}</span>
                          <span className="min-w-0">
                            <span className="block font-semibold text-slate-800 group-hover:text-[#1E9E2A]">{r.student_name}</span>
                            <span className="block text-xs text-slate-500 truncate max-w-[240px]">{r.email}</span>
                          </span>
                        </Link>
                      </TableCell>
                      <TableCell title={r.course_title}>
                        {/* o nome comprido do Método se repete em todas as linhas: mostra só o que muda (Nível Básico, Avançado...) */}
                        {(() => { const m = /^(?:Curso\s+)?Cabeleireiro Cient[ií]fico Formulador\s*[-–]\s*(.+)$/i.exec(r.course_title || "");
                          return m ? <span className="inline-flex whitespace-nowrap rounded-lg bg-[#232D4F]/[.06] px-2.5 py-1 text-xs font-semibold text-[#232D4F]">{m[1].replace(/^N[ií]vel /i, "Nível ")}</span>
                            : <span className="text-sm text-slate-700">{r.course_title}</span>; })()}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-xs text-slate-500">
                        {[r.bonus && "Bônus presencial", r.direct && "Direta", ...r.groups.map((g) => `Turma: ${g}`)].filter(Boolean).join(" · ") || "—"}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-slate-500">{fmtDate(r.starts_at) || "—"}</TableCell>
                      <TableCell className="text-slate-700">{fmtDate(r.expires_at) || "Sem prazo"}</TableCell>
                      <TableCell data-testid={`last-access-${r.student_id}`}>
                        {(() => { const u = ultimoAcesso(r.last_access); return (<div className="whitespace-nowrap"><div className={`text-sm font-medium ${u.cls}`}>{u.txt}</div>{u.sub && <div className="text-[11px] text-slate-400">{u.sub}</div>}</div>); })()}
                      </TableCell>
                      <TableCell><span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${meta.cls}`}>{meta.label}</span></TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu modal={false}>
                          <DropdownMenuTrigger asChild>
                            <button className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100" title="Opções"><MoreVertical className="h-4 w-4" /></button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem onSelect={() => setStudent({ id: r.student_id, name: r.student_name })}><Pencil className="h-4 w-4 mr-2" /> Editar datas</DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {[30, 90, 365].map((n) => (
                              <DropdownMenuItem key={n} onSelect={() => extend(r, n)}><CalendarPlus className="h-4 w-4 mr-2" /> Prolongar +{n} dias</DropdownMenuItem>
                            ))}
                            {r.expires_at && <DropdownMenuItem onSelect={() => vitalicio(r)} className="text-[#926B38]"><CalendarPlus className="h-4 w-4 mr-2" /> Tornar vitalício</DropdownMenuItem>}
                            <DropdownMenuSeparator />
                            {r.status === "bloqueada" ? (
                              <DropdownMenuItem onSelect={() => setStatus(r, "ativa")} className="text-green-700"><CheckCircle className="h-4 w-4 mr-2" /> Liberar</DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onSelect={() => setStatus(r, "bloqueada")} className="text-red-600"><Ban className="h-4 w-4 mr-2" /> Bloquear</DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <Pager page={page} perPage={50} total={data.total} onChange={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: "smooth" }); }} testid="enrollments-pager" />
          </div>
        )}
      </div>

      <NewEnrollmentDialog open={newOpen} courses={courses} onClose={() => setNewOpen(false)} onDone={load} />
      <EnrollmentsDialog student={student} onClose={() => setStudent(null)} onChanged={load} />
    </div>
  );
}
